import { BaseService } from '@/services/base/BaseService';
import { TestType } from '@/models/SharedTypes';
import { EducationLevel, PreferredStudyTime, ChallengingTopic, LearningStyle, type ExamScheduleEntry, type RetakeReport } from '@/models/UserTypes';
import type { LicensingAnswers } from '@/features/licensing/types';

/**
 * Extends `LicensingAnswers` (#411) so the jurisdiction step's fields ride the
 * same callable as the rest of the profile. See UserService for why the
 * licensing half is a shared type rather than seven more hand-typed keys.
 */
export interface UpdateProfileRequest extends LicensingAnswers {
  examDate?: string;
  examDatePromptDismissed?: boolean;
  // Legacy: set exam date for specific test type (superseded by examScheduleForTestType)
  examDateForTestType?: {
    testType: TestType;
    date: string | null;  // null to clear the date
  };
  // New: set the status-first schedule for a specific test type. `entry: null`
  // clears it. Server stamps updatedAt (see aswb-backend PR #102).
  examScheduleForTestType?: {
    testType: TestType;
    entry: ExamScheduleEntry | null;
  };
  // New: dismiss prompt for specific exam type
  dismissExamPromptFor?: TestType;
  name?: string;
  studyGoal?: string;
  onboardingCompleted?: boolean;
  targetExam?: TestType;
  educationLevel?: EducationLevel;
  preferredStudyTime?: PreferredStudyTime;
  challengingTopics?: ChallengingTopic[];
  learningStyle?: LearningStyle;
  schoolCode?: string;
  /**
   * The trial doorway's "I don't have a date yet" answer (#225): distinct from
   * simply omitting an exam date, so the plan can tell "hasn't answered yet"
   * from "answered: no date". 'none' is the only value written today.
   */
  examDateStatus?: 'none';
  /** Hour of day (0-23) the doorway's preferred-study-time answer maps to. */
  studyHour?: number;
  /** IANA zone (Intl.DateTimeFormat().resolvedOptions().timeZone), captured silently. */
  timezone?: string;
  /**
   * Prior-attempt report from the optional onboarding retake step. Omit the key
   * entirely for a first-timer — a skipped step writes nothing. Shape pinned with
   * aswb-backend (feat/engine-prior-diagnostic-retake).
   */
  retakeReport?: RetakeReport;
  diagnostic?: {
    score: number;
    total: number;
    readiness: number;
    weakAreas: string[];
    examType: string | null;
    completedAt: string;
  };
}

export interface UpdateProfileResponse {
  success: boolean;
  message: string;
  updatedFields: string[];
}

export class ProfileService extends BaseService {
  private static instance: ProfileService;

  public static getInstance(): ProfileService {
    if (!ProfileService.instance) {
      ProfileService.instance = new ProfileService();
    }
    return ProfileService.instance;
  }

  /**
   * Update user profile information
   */
  public async updateProfile(data: UpdateProfileRequest): Promise<UpdateProfileResponse> {
    return this.callFunction<UpdateProfileRequest, UpdateProfileResponse>(
      'updateUserProfile',
      data,
      {
        timeout: 30000,
        retry: true
      }
    );
  }

  /**
   * Dismiss the exam date prompt (user chose not to set a date right now)
   * @deprecated Use dismissExamDatePromptFor(testType) for multi-exam support
   */
  public async dismissExamDatePrompt(): Promise<UpdateProfileResponse> {
    return this.updateProfile({ examDatePromptDismissed: true });
  }

  /**
   * Set exam date for a specific test type
   * @param testType - The test type (LCSW, LMSW, LBSW)
   * @param date - ISO date string or null to clear the date
   */
  public async setExamDateForTestType(
    testType: TestType,
    date: string | null
  ): Promise<UpdateProfileResponse> {
    return this.updateProfile({
      examDateForTestType: { testType, date }
    });
  }

  /**
   * Dismiss the exam date prompt for a specific test type
   * @param testType - The test type to dismiss the prompt for
   */
  public async dismissExamDatePromptFor(testType: TestType): Promise<UpdateProfileResponse> {
    return this.updateProfile({ dismissExamPromptFor: testType });
  }

  /**
   * Set (or clear) the status-first exam schedule for a specific test type.
   * This is the write path the scheduler UX uses everywhere (onboarding +
   * dashboard). The server stamps `updatedAt`.
   * @param testType - The track (LCSW/LMSW/LBSW).
   * @param entry - The schedule entry, or null to clear it.
   */
  public async setExamScheduleForTestType(
    testType: TestType,
    entry: ExamScheduleEntry | null
  ): Promise<UpdateProfileResponse> {
    return this.updateProfile({
      examScheduleForTestType: { testType, entry }
    });
  }
}

// Export singleton instance
export const profileService = ProfileService.getInstance();